import { useMemo, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Progress,
  Space,
  Steps,
  Tag,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  LinkOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { RelatedDrawingsDrawer } from '@/components/RelatedDrawingsDrawer'
import { SpaceReviewPanel } from '@/components/SpaceReviewPanel'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { jobsApi } from '@/api/jobs'
import { spacesApi } from '@/api/spaces'
import { reportsApi } from '@/api/reports'
import {
  JOB_STATE_COLORS,
  JOB_STATE_LABELS,
  WORKFLOW_STEP_LABELS,
  isJobInFlight,
  type SpaceReviewItem,
  type UpdateSpaceRequest,
} from '@/types/api'

const { Title, Text } = Typography

const WORKFLOW_STEPS = [
  'drawing_classify',
  'cad_parse',
  'cad_quality_check',
  'layer_classify',
  'plan_index_extract',
  'space_detect',
  'plan_target_binding',
  'ai_assist',
  'process_note_extract',
  'fixture_legend_extract',
  'fixture_count',
  'door_schedule_extract',
  'door_count',
  'new_wall_extract',
  'material_catalog_extract',
  'elevation_finish_extract',
  'wait_review',
  'quantity_calculate',
  'validate',
  'report_generate',
  'done',
]

export function JobReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = App.useApp()
  const [relatedOpen, setRelatedOpen] = useState(false)

  // ---- Job 状态轮询（未终态时每 2 秒轮询）----
  const { data: job } = useQuery({
    queryKey: ['job', id],
    queryFn: () => jobsApi.get(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const s = q.state.data?.state
      if (!s) return 2000
      if (['success', 'partial_success', 'failed', 'cancelled'].includes(s)) return false
      return 2000
    },
  })

  const canReview = job?.state === 'wait_review'
  const isRunning = job?.state === 'processing' || job?.state === 'created' || job?.state === 'queued'
  const isFinal = job && ['success', 'partial_success'].includes(job.state)
  const isFailed = job?.state === 'failed'
  const isCancelled = job?.state === 'cancelled'
  const canStop = !!job && isJobInFlight(job.state)

  // ---- 空间列表（wait_review 或已完成时可查看）----
  const { data: spaces } = useQuery({
    queryKey: ['spaces', id],
    queryFn: () => spacesApi.list(id!),
    enabled: !!id && !!job && ['wait_review', 'success', 'partial_success'].includes(job.state),
  })

  const updateSpaceMutation = useMutation({
    mutationFn: async (params: {
      spaceId: string
      version: number
      updates: Partial<Pick<SpaceReviewItem, 'name' | 'space_type' | 'is_deleted'>> & {
        param_overrides?: Record<string, number>
      }
    }) =>
      spacesApi.update(id!, params.spaceId, {
        version: params.version,
        ...params.updates,
      } as UpdateSpaceRequest),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['spaces', id] })
      qc.invalidateQueries({ queryKey: ['space-bill', id] })
    },
  })

  const reviewCompleteMutation = useMutation({
    mutationFn: () => jobsApi.reviewComplete(id!),
    onSuccess: () => {
      message.success('复核完成，继续算量...')
      qc.invalidateQueries({ queryKey: ['job', id] })
    },
  })

  const cancelJobMutation = useMutation({
    mutationFn: () => jobsApi.cancel(id!),
    onSuccess: (job) => {
      message.success('任务已停止')
      qc.invalidateQueries({ queryKey: ['job', id] })
      qc.invalidateQueries({ queryKey: ['jobs', job.project_id] })
    },
  })

  const { data: spaceBill, isLoading: spaceBillLoading } = useQuery({
    queryKey: ['space-bill', id],
    queryFn: () => jobsApi.spaceBill(id!),
    enabled: !!id && !!job && ['wait_review', 'success', 'partial_success'].includes(job.state),
    retry: false,
  })

  // ---- Report ----
  const { data: reportMeta } = useQuery({
    queryKey: ['report-meta', id],
    queryFn: () => reportsApi.meta(id!),
    enabled: !!id && isFinal,
    retry: false,
  })

  // ---- 步骤条 index ----
  const currentStep = useMemo(() => {
    if (!job) return 0
    if (job.state === 'success' || job.state === 'partial_success') return WORKFLOW_STEPS.length - 1
    if (job.state === 'failed' || job.state === 'cancelled') return -1
    if (job.state === 'wait_review') return WORKFLOW_STEPS.indexOf('wait_review')
    const idx = job.current_skill ? WORKFLOW_STEPS.indexOf(job.current_skill) : 0
    return Math.max(0, idx)
  }, [job])

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => job && navigate(`/projects/${job.project_id}`)}
        style={{ padding: 0, marginBottom: 8 }}
      >
        返回项目
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Title level={3} style={{ marginTop: 0 }}>
          算量任务 <Text code>{id?.slice(0, 8)}</Text>
        </Title>
        <Space>
          {canStop && (
            <Popconfirm
              title="停止后才能开始新任务，确定停止？"
              okText="停止"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => cancelJobMutation.mutate()}
            >
              <Button
                danger
                icon={<StopOutlined />}
                loading={cancelJobMutation.isPending}
              >
                停止任务
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<LinkOutlined />}
            onClick={() => setRelatedOpen(true)}
            disabled={!job || ['created', 'queued'].includes(job.state)}
          >
            相关图纸 / 索引
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['job', id] })
              qc.invalidateQueries({ queryKey: ['spaces', id] })
              qc.invalidateQueries({ queryKey: ['space-bill', id] })
            }}
          >
            刷新
          </Button>
        </Space>
      </div>

      {/* 顶部状态卡 */}
      {job && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={4} size="small">
            <Descriptions.Item label="状态">
              <Tag color={JOB_STATE_COLORS[job.state]}>
                {JOB_STATE_LABELS[job.state] || job.state}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="当前步骤">
              {job.current_skill
                ? (WORKFLOW_STEP_LABELS[job.current_skill] || job.current_skill)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="进度">
              <Progress
                percent={job.progress}
                size="small"
                status={isFailed ? 'exception' : isFinal ? 'success' : 'active'}
                style={{ width: 200 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              <Text type="secondary" style={{ fontSize: 12 }}>
                {job.started_at ? new Date(job.started_at).toLocaleString('zh-CN') : '-'}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {isRunning && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="任务在后台继续执行。关闭本页不影响进度，之后可从项目里点进该任务查看最新状态并复核。"
            />
          )}

          {job.error_message && job.state !== 'cancelled' && (
            <Alert
              type="error"
              message={`${job.error_code || '错误'}: ${job.error_message}`}
              style={{ marginTop: 12 }}
              showIcon
            />
          )}
        </Card>
      )}

      {/* 步骤条 */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <Steps
          current={currentStep}
          size="small"
          status={isFailed ? 'error' : isRunning ? 'process' : 'finish'}
          items={WORKFLOW_STEPS.map((s) => ({
            title: WORKFLOW_STEP_LABELS[s] || s,
          }))}
        />
      </Card>

      {(canReview || isFinal) && spaces && id && (
        <SpaceReviewPanel
          jobId={id}
          spaces={spaces}
          spaceBill={spaceBill}
          spaceBillLoading={spaceBillLoading}
          canReview={canReview}
          isFinal={!!isFinal}
          reviewPending={reviewCompleteMutation.isPending}
          updatePending={updateSpaceMutation.isPending}
          onReviewComplete={() => reviewCompleteMutation.mutate()}
          onShopfrontApplied={() => {
            qc.invalidateQueries({ queryKey: ['spaces', id] })
            qc.invalidateQueries({ queryKey: ['review-map', id] })
            qc.invalidateQueries({ queryKey: ['space-bill', id] })
          }}
          onUpdate={(params) => updateSpaceMutation.mutate(params)}
        />
      )}

      {/* 报告下载 */}
      {isFinal && (
        <Card
          title={
            <Space>
              <span>报告下载</span>
              <Tag color="green">
                {JOB_STATE_LABELS[job.state]}
              </Tag>
            </Space>
          }
        >
          {reportMeta ? (
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                icon={<FileExcelOutlined />}
                onClick={() => reportsApi.downloadXlsx(id!)}
              >
                下载 Excel（总汇总/按房间）
              </Button>
              <Button
                size="large"
                icon={<FileTextOutlined />}
                onClick={() => reportsApi.downloadJson(id!)}
              >
                下载 JSON
              </Button>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                生成时间：{reportMeta.generated_at ? new Date(reportMeta.generated_at).toLocaleString('zh-CN') : '-'}
              </Text>
            </Space>
          ) : (
            <Alert message="报告正在生成中..." type="info" showIcon />
          )}
        </Card>
      )}

      {isCancelled && (
        <Alert
          message="任务已停止"
          description="可返回项目后重新开始算量。"
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          action={
            <Button
              size="small"
              onClick={() => job && navigate(`/projects/${job.project_id}`)}
            >
              返回项目
            </Button>
          }
        />
      )}

      {isFailed && (
        <Alert
          message="任务失败"
          description={
            <>
              <div>{job.error_message}</div>
              <div style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => job && navigate(`/projects/${job.project_id}`)}
                >
                  返回项目重新触发
                </Button>
              </div>
            </>
          }
          type="error"
          showIcon
        />
      )}

      {id && (
        <RelatedDrawingsDrawer
          jobId={id}
          open={relatedOpen}
          onClose={() => setRelatedOpen(false)}
        />
      )}
    </>
  )
}
